import { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNetworkStatus } from '@/api'
import { useActor } from '@/auth/pin/phoneSession'
import { downloadCaches, roster, useSyncStatus } from '@/offline'
import { useGate } from '../data/useGate'
import { Footer, Icon, ItemRow, Strip, Tick, V1Header, V1Screen, WaitCta } from './kit'
import { C } from './style'
import { tr } from './text'

/**
 * Multi-village phones (V1): after the PIN, the operator picks the village she works in today.
 * One village (or none on record) → straight on. Another village moves the phone there and
 * downloads its farmer list (needs the internet); never while a day is open or a purchase is
 * half done on this phone — close that day first.
 */
export function V1Village() {
  const navigate = useNavigate()
  const location = useLocation()
  const actor = useActor()
  const g = useGate()
  const sync = useSyncStatus()
  const { online } = useNetworkStatus()
  // null = not on this roster (then nothing to pick); undefined = still loading.
  const member = useLiveQuery(
    async () => (await roster.teamMember(actor.user_id)) ?? null,
    [actor.user_id],
  )
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const from = (location.state as { from?: string } | null)?.from ?? '/m'

  if (!g.ready || member === undefined) return <V1Screen />
  const reg = g.info.registration
  const current = reg?.team
  const villages = member?.villages ?? []
  const onlyCurrent =
    !current ||
    current.kind !== 'VILLAGE' ||
    villages.length === 0 ||
    (villages.length === 1 && villages[0]!.id === current.id)
  if (onlyCurrent) return <Navigate to={from} replace />

  // A day or a purchase in progress belongs to this village: finish it here first.
  const openDay = g.app ?? g.paper
  const halfDone = g.drafts.length > 0

  const pick = async (id: string) => {
    setError(null)
    if (id === current.id) {
      navigate(from, { replace: true })
      return
    }
    if (openDay || halfDone) {
      setError(tr('gate.v1.village.closeFirst', { village: current.name }))
      return
    }
    if (!online) {
      setError(tr('gate.v1.village.needNet'))
      return
    }
    setBusy(id)
    try {
      await downloadCaches(undefined, { groupId: id })
      navigate('/m/gate', { replace: true })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <V1Screen
      main={{ gap: 8 }}
      header={<V1Header pending={sync.pending} icon="home" title={tr('gate.v1.village.title')} />}
      footer={
        <Footer>
          <WaitCta>{tr('gate.v1.village.tap')}</WaitCta>
        </Footer>
      }
    >
      <Strip icon="user">{tr('gate.v1.village.line', { name: actor.name })}</Strip>
      {(openDay || halfDone) && (
        <Strip icon="bang">{tr('gate.v1.village.closeFirst', { village: current.name })}</Strip>
      )}
      {villages.map((v) => {
        const here = v.id === current.id
        return (
          <ItemRow
            key={v.id}
            onClick={busy ? undefined : () => void pick(v.id)}
            border={here ? C.ok : C.rule}
            borderWidth={here ? 2 : 1.5}
            lead={<Icon name="home" size={28} />}
            titleSize={20}
            title={v.name}
            line={
              <span style={{ fontSize: 15, color: C.muted }}>
                {busy === v.id
                  ? tr('gate.v1.village.loading')
                  : here
                    ? tr('gate.v1.village.current')
                    : tr('gate.v1.village.switch')}
              </span>
            }
            right={here ? <Tick /> : undefined}
          />
        )
      })}
      {error && <Strip icon="bang">{error}</Strip>}
    </V1Screen>
  )
}
